import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";

const privacySections = [
  ["privacy-overview", "Überblick"],
  ["privacy-controller", "Verantwortlicher"],
  ["privacy-processing", "Welche Daten wir verarbeiten"],
  ["privacy-storage", "Warenkorb & lokale Speicherung"],
  ["privacy-rights", "Deine Rechte"],
];

function Privacy() {
  const { brandName, contact = {} } = siteConfig;
  const { email, phone, phoneHref, address } = contact;

  return (
    <main className="privacy">
      <header className="privacy__hero">
        <div className="privacy__hero-inner">
          <nav className="privacy__breadcrumb" aria-label="Brotkrümelnavigation">
            <Link to="/">Startseite</Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">Datenschutz</span>
          </nav>
          <p className="privacy__eyebrow">Transparenz &amp; Vertrauen</p>
          <h1>Datenschutzhinweise</h1>
          <p className="privacy__intro">
            Wir erklären verständlich, welche personenbezogenen Daten bei der
            Nutzung unseres Shops entstehen und wie {brandName} damit umgeht.
          </p>
          <p className="privacy__updated">Stand: August 2026</p>
        </div>
      </header>

      <div className="privacy__layout">
        <aside className="privacy__toc" aria-label="Inhaltsverzeichnis">
          <p>Auf dieser Seite</p>
          <ol>
            {privacySections.map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`}>{label}</a>
              </li>
            ))}
          </ol>
        </aside>

        <article className="privacy__article">
          <section id="privacy-overview">
            <span className="privacy__number" aria-hidden="true">01</span>
            <h2>Datenschutz auf einen Blick</h2>
            <p>
              Personenbezogene Daten sind alle Informationen, über die eine
              Person direkt oder indirekt identifiziert werden kann. Wir
              verarbeiten nur Daten, die für den Betrieb des Shops, die
              Bearbeitung einer Anfrage oder die Abwicklung einer Bestellung
              erforderlich sind.
            </p>
            <div className="privacy__notice">
              <strong>Unser Grundsatz</strong>
              <p>
                So wenig Daten wie möglich, so transparent wie möglich und nur
                so lange gespeichert, wie es für den jeweiligen Zweck nötig ist.
              </p>
            </div>
          </section>

          <section id="privacy-controller">
            <span className="privacy__number" aria-hidden="true">02</span>
            <h2>Verantwortlicher und Kontakt</h2>
            <p>
              Verantwortlich für die Verarbeitung personenbezogener Daten in
              diesem Onlineshop ist {brandName}.
            </p>
            <address className="privacy__contact-card">
              <strong>{brandName}</strong>
              {address && <span>{address}</span>}
              {email && <a href={`mailto:${email}`}>{email}</a>}
              {phone && (
                <a href={`tel:${phoneHref || phone.replace(/[^+\d]/g, "")}`}>
                  {phone}
                </a>
              )}
            </address>
          </section>

          <section id="privacy-processing">
            <span className="privacy__number" aria-hidden="true">03</span>
            <h2>Welche Daten wir verarbeiten</h2>
            <p>
              Art und Umfang der Verarbeitung hängen davon ab, wie du unseren
              Shop nutzt. Die wichtigsten Fälle findest du in dieser Übersicht.
            </p>
            <div className="privacy__table-wrap" tabIndex="0">
              <table>
                <caption className="privacy__visually-hidden">
                  Übersicht der Datenverarbeitung
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Situation</th>
                    <th scope="col">Daten</th>
                    <th scope="col">Zweck</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Shopbesuch</th>
                    <td>Technische Verbindungs- und Gerätedaten</td>
                    <td>Sicherer, stabiler Seitenbetrieb</td>
                  </tr>
                  <tr>
                    <th scope="row">Bestellung</th>
                    <td>Kontakt-, Liefer- und Bestelldaten</td>
                    <td>Vertrag und Lieferung abwickeln</td>
                  </tr>
                  <tr>
                    <th scope="row">Kontakt</th>
                    <td>Von dir eingegebene Angaben und Nachricht</td>
                    <td>Anfrage beantworten</td>
                  </tr>
                  <tr>
                    <th scope="row">Warenkorb</th>
                    <td>Artikel, Variante und Menge</td>
                    <td>Auswahl auf deinem Gerät erhalten</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>Rechtsgrundlagen</h3>
            <p>
              Je nach Vorgang beruht die Verarbeitung auf der Erfüllung eines
              Vertrags, vorvertraglichen Maßnahmen, einer rechtlichen Pflicht,
              einem berechtigten Interesse am sicheren Shopbetrieb oder deiner
              freiwilligen Einwilligung. Eine Einwilligung kannst du jederzeit
              mit Wirkung für die Zukunft widerrufen.
            </p>

            <h3>Empfänger und Dienstleister</h3>
            <p>
              Daten werden nur weitergegeben, wenn dies für Lieferung,
              Zahlungsabwicklung, technische Bereitstellung oder die Erfüllung
              gesetzlicher Pflichten erforderlich ist. Beauftragte Dienstleister
              dürfen Daten ausschließlich nach Weisung und für den vereinbarten
              Zweck verarbeiten.
            </p>
          </section>

          <section id="privacy-storage">
            <span className="privacy__number" aria-hidden="true">04</span>
            <h2>Warenkorb und lokale Speicherung</h2>
            <p>
              Damit deine Auswahl bei einem erneuten Seitenaufruf erhalten
              bleibt, speichert der Shop den Warenkorb lokal in deinem Browser.
              Diese Information wird auf deinem Gerät abgelegt und kann über die
              Browsereinstellungen entfernt werden.
            </p>
            <ul>
              <li>Gespeichert werden Produkt-ID, Variante und Stückzahl.</li>
              <li>Es werden dabei keine Zahlungsdaten lokal gespeichert.</li>
              <li>Das Löschen der Browserdaten entfernt auch den Warenkorb.</li>
            </ul>

            <h3>Speicherdauer</h3>
            <p>
              Wir löschen oder anonymisieren Daten, sobald ihr Zweck entfällt
              und keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
              Vertrags- und Buchungsunterlagen können gesetzlichen Fristen
              unterliegen; reine Kontaktanfragen werden regelmäßig früher
              entfernt.
            </p>
          </section>

          <section id="privacy-rights">
            <span className="privacy__number" aria-hidden="true">05</span>
            <h2>Deine Datenschutzrechte</h2>
            <p>Im gesetzlichen Rahmen kannst du insbesondere verlangen:</p>
            <ul>
              <li>Auskunft über deine gespeicherten Daten,</li>
              <li>Berichtigung unzutreffender Angaben,</li>
              <li>Löschung oder Einschränkung der Verarbeitung,</li>
              <li>Herausgabe übertragbarer Daten,</li>
              <li>Widerspruch gegen bestimmte Verarbeitungen.</li>
            </ul>
            <p>
              Außerdem kannst du dich bei einer zuständigen
              Datenschutzaufsichtsbehörde beschweren. Für Fragen oder zur
              Ausübung deiner Rechte nutze bitte unsere Kontaktmöglichkeiten.
            </p>
            <Link className="privacy__button" to="/contact">
              Datenschutzanfrage senden
            </Link>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
